import { Injectable } from '@angular/core';
import {BehaviorSubject, combineLatestWith, Observable, Subject, switchMap} from 'rxjs';
import {filter, map, take, tap, withLatestFrom} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import { ManagementStoreService } from './management-store.service';
import { ManagementApiService } from '@emm/management/services/management-api.service';
import { ItemsFacadeService } from '@emm/management/services/items-facade.service';
import {genericSort} from '@emm/core/util/sort.util';
import {ItemGroupingsEffects} from '@emm/management/services/effects/item-groupings.effects';
import {Item} from '@emm/core/models/item.model';
import {isGroupItemType} from '@emm/core/models/item-type.model';
import {Node} from '@emm/core/models/node.model';


@Injectable({
    providedIn: 'root'
})
export class ItemGroupingsFacadeService {

    centerLoading$ = this.managementStore.isLoading$.itemTypes.pipe(
        combineLatestWith(this.managementStore.isLoading$.itemGroupings),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );

    itemDetail$ = new BehaviorSubject<any>(null);

    rightLoading$ = this.managementStore.isLoading$.base;

    anyLoading$ = this.centerLoading$.pipe(combineLatestWith(this.rightLoading$),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );
    attributes$ = this.api.getAllGlobalAttributes().pipe(
        tap(globalAttributes => {
            for (const attribute of globalAttributes) {
                if ((attribute?.type === 'SELECT' || attribute?.type === 'MULTI_SELECT' || attribute?.type === 'BOOL')
                    && attribute?.options) {
                    const lengthOfOptionsArray = attribute.options.length;
                    let fixedOption;
                    Object.values(attribute?.options).forEach((option: string) => {
                        let cleanString = this.removeInvalidCharacters(option);
                        if (attribute.type === 'BOOL') {
                            cleanString = cleanString.charAt(0).toUpperCase() + cleanString.slice(1);
                        }
                        fixedOption = {label: cleanString, value: cleanString};
                        attribute.options.push(fixedOption);
                        for (let i = 0; i < lengthOfOptionsArray; i++) {
                            if (typeof attribute.options[i] !== 'object') {
                                attribute.options.splice(i, 1);
                            }
                        }
                    });
                }
            }
        })
    );
    itemTypes$ = this.api.getAllLegacyItemTypes().pipe(
        map((types) => types.filter(isGroupItemType))
    );
    itemGroupings$ = this.api.getItemGroupings();
    itemGroupingsAsNodes$ = this.itemGroupings$.pipe(
        filter(RA.isNotNil),
        map((items) =>
            items.sort(genericSort(a => a['name']))
                .map(ItemGroupingsEffects.mapItemGroupingsToNode)),
        );
    updateItemGrouping$ = this.managementStore.updateItem$; // uses the item form

    updateNode$ = new Subject<string>();

    constructor(
        private itemGroupingsEffects: ItemGroupingsEffects,
        private itemsFacade: ItemsFacadeService,
        private managementStore: ManagementStoreService,
        public api: ManagementApiService
    ) {}

    init(): void {
        this.managementStore.dispatch.initItemGroupings();
    }

    removeInvalidCharacters(input: string): string {
        return input.replace(/[^a-zA-Z0-9_&!\/\-\s]/g, '');
    }

    selectItemGrouping(tag: string): void {
        this.api.getItem(tag).subscribe(result => {
            this.itemDetail$.next(result);
        });
    }

    openAddDialog(): void {
        this.managementStore.dispatch.openAddItemGroupingsDialog();
    }

    getAllChildItems(id: string): Observable<Node<Item>[]> {
        const success$ = this.api.getAllChildItems(id).pipe(
            map((items: Item[]) =>
                RA.isNonEmptyArray(items) ? items.map(ItemGroupingsEffects.mapItemGroupingsToNode) : []
            )
        );
        return success$.pipe(take(1));
    }

    toggleItemDisabled(tag: string, enabled: boolean): any {
        this.api.setEnabled(tag, enabled).subscribe();
    }

    addChildItems(parentTag: string, children: Item[], isGlobalAdd: boolean): any {
        this.api.addChildItems(parentTag, children, isGlobalAdd).subscribe();
    }

    deleteItemGroup(tag: string): any {
        this.api.deleteItem(tag).subscribe();
    }

    removeChildItem(parentTag: string, childTag: string, isGlobalDelete: boolean): any {
        this.api.removeChildItem(parentTag, childTag).subscribe();
    }

    updateNodeInItemGrouping(updatedTag: string): void {
        this.selectItemGrouping(updatedTag);
        this.updateNode$.next(updatedTag);
    }

}
